import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Site-wide scroll-reveal — mounted ONCE in Layout.
 *
 * On every route it gently fades-up the page's `<section>` blocks as they
 * scroll into view, so EVERY page gets the effect without wrapping each one
 * by hand. Pages built ENTIRELY with `<div>` (Blog, Category, Comparatifs,
 * Carte, Galerie, LeadMagnet, outils, légal…) carry no `<section>`, so we
 * fall back to revealing their top-level content blocks — no page is left
 * un-animated. Design notes :
 *
 *   - Opacity ONLY (no transform) → never creates a containing block that
 *     would trap `position: fixed` descendants (the article-popup bug).
 *   - Skips sections already managed by <RevealOnScroll> (Home / Article /
 *     Shop) — those live inside a wrapper that carries an inline `opacity`
 *     style, so we bail if any ancestor has one (avoids double-handling).
 *   - Handles lazy-loaded routes : a debounced MutationObserver re-scans
 *     `#main` as route content mounts.
 *   - FAIL-OPEN : any section still hidden after a short delay is force-shown,
 *     so content is NEVER left invisible if the observer/scroll misbehaves.
 *   - Fully disabled under `prefers-reduced-motion` (content shown as-is).
 *
 * Returns null — it only wires side-effects.
 */
export default function AutoReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const main = document.getElementById('main');
    if (!main) return undefined;

    const observed = new Set();

    const inView = (el) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.94 && r.bottom > 0;
    };

    let io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) { e.target.classList.add('ip-shown'); io.unobserve(e.target); }
          });
        },
        { rootMargin: '0px 0px -6% 0px' },
      );
    }

    const process = (el) => {
      if (observed.has(el)) return;
      if (el.dataset.ipNoReveal !== undefined) return;
      // Skip sections already reveal-managed (RevealOnScroll wrapper carries an
      // inline opacity style) to avoid double-animating Home / Article / Shop.
      if (el.closest('[style*="opacity"]')) return;
      observed.add(el);
      el.classList.add('ip-auto-reveal');
      if (inView(el)) { el.classList.add('ip-shown'); return; }
      if (io) io.observe(el);
    };

    // Pages with no <section> (built only with <div>) → descend through pure
    // single-child wrappers (e.g. a lone <Container>) to the first node that
    // holds several block children. Those children are the page's real content
    // blocks (header, filters, grid…).
    const fallbackBlocks = () => {
      let node = main;
      while (node.children.length === 1 && node.firstElementChild) {
        node = node.firstElementChild;
      }
      return Array.from(node.children);
    };

    // Tailwind card grids carry a bare `grid` class token.
    const GRID = '[class~="grid"]';

    const scan = () => {
      // 1) Explicit blocks: every <section> + any opt-in [data-reveal] element.
      main.querySelectorAll('section, [data-reveal]').forEach(process);
      // 2) Catch content NOT wrapped in a <section> — div-only pages (Blog,
      //    Category, Comparatifs, Carte, Galerie, légal…) AND the div parts of
      //    a mixed page — so nothing is left un-animated. Skip top-level blocks
      //    that ARE or CONTAIN a <section>/[data-reveal] (handled in step 1) to
      //    avoid fading a wrapper on top of its own sections. Card grids are
      //    expanded to their items so below-the-fold cards stagger in on scroll.
      fallbackBlocks().forEach((block) => {
        if (block.matches('section, [data-reveal]') || block.querySelector('section, [data-reveal]')) return;
        const grids = block.matches(GRID) ? [block] : Array.from(block.querySelectorAll(GRID));
        if (grids.length) {
          grids.forEach((g) => { if (g.children.length >= 2) Array.from(g.children).forEach(process); });
        } else {
          process(block);
        }
      });
    };

    // Initial pass + a debounced re-scan for lazy route content mounting in.
    scan();
    let scheduled = false;
    const scheduleScan = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; scan(); });
    };
    const mo = new MutationObserver(scheduleScan);
    mo.observe(main, { childList: true, subtree: true });

    // Redundant safety: reveal on scroll for anything IO might miss.
    const onScroll = () => {
      observed.forEach((el) => {
        if (!el.classList.contains('ip-shown') && inView(el)) el.classList.add('ip-shown');
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Fail-open: never leave content invisible.
    const failOpen = setTimeout(() => {
      observed.forEach((el) => el.classList.add('ip-shown'));
    }, 2500);

    return () => {
      mo.disconnect();
      if (io) io.disconnect();
      clearTimeout(failOpen);
      window.removeEventListener('scroll', onScroll);
    };
  }, [pathname]);

  return null;
}
